import React, { useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import moment from 'moment';
import { Container, Stack } from 'react-bootstrap';

import { UniqueWidgetRefreshContext } from 'App';
import DropDownSpread from 'components/DropDownSpread';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import { useAPIData } from 'hooks/useAPIData';
import useDataRefresh from 'hooks/useDataRefresh';
import { DateToString } from 'utils/stringUtils';
import { RiWindyFill, RiDropLine } from 'react-icons/ri';
import { WiSprinkle } from 'react-icons/wi';
import { BsArrowUp, BsArrowDown, BsClock, BsCompass } from 'react-icons/bs';
import { FaEye } from 'react-icons/fa';
import { FaTemperatureHalf } from 'react-icons/fa6';
import { getBuoyConditions } from 'services/weather/marine.service';

const ROUTE = 'getBuoyConditions';

const ICONS = [
  <RiWindyFill />,
  <WiSprinkle />,
  <BsClock />,
  <BsArrowUp />,
  <RiDropLine />,
  <BsArrowDown />,
  <FaTemperatureHalf />,
  <BsCompass />,
  <FaEye />,
];

export default function BuoyConditions() {
  const currentDateStr = DateToString(new Date());

  const [data, setData] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const api = useAPIData();
  const refreshTokens = useDataRefresh();
  const uwf = useContext(UniqueWidgetRefreshContext);

  const locationKey = `${Cookies.get('city')}-${Cookies.get('state')}`;
  const uniqueKey = `${locationKey}-${ROUTE}`;

  const mappedData = useMemo(
    () =>
      data.map((item) => ({
        key: `${item.buoy_id}-${item.utcTime}`,
        title: (
          <div className="d-flex justify-content-between w-100">
            <span>{item.buoy_name}</span>
            <span>{moment(item.utcTime).format('MM/DD/YYYY')}</span>
          </div>
        ),
        payload: {
          'Wind SP': `${item.wind_speed} MPH`,
          'Dew Point': `${item.dew_point}°F`,
          'Wave Period': item.wave_period,
          'Wave Height': item.wave_height,
          'Water Temp': `${item.water_temperature}°F`,
          'Barometric pressure': `${item.baro_pressure} in`,
          'Air Temp': `${item.air_temperature}°F`,
          'Wind Direction': item.wind_short,
          Visibility: `${item.visibility} mi`,
        },
      })),
    [data],
  );

  const loadData = async (force = false) => {
    const hasExistingData = data.length > 0;

    if (!hasExistingData) {
      setIsInitialLoad(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      const requester = force ? api.forceGet : api.get;
      const response = await requester({
        key: ROUTE,
        apiFunction: getBuoyConditions,
      });
      setData(response?.data?.result ?? []);
    } catch (error) {
      console.error('Error fetching buoy conditions:', error);
      setData([]);
    } finally {
      setIsInitialLoad(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!uwf[uniqueKey]) return;
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uwf, uniqueKey]);

  useEffect(() => {
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTokens.widgetRefresh]);

  if (isInitialLoad) {
    return <WidgetLoadingAnimation />;
  }

  return (
    <Container
      fluid
      className="d-flex flex-column h-100 px-0 user-select-none text-white overflow-auto"
    >
      <WidgetTimeUpdate route={ROUTE} dataUpdateOnly addMargin />

      <div className="border-bottom border-secondary-subtle pb-2 mb-4 fs-5">
        {`${currentDateStr.month} ${currentDateStr.day}, ${currentDateStr.year} Buoy Conditions`}
        {isRefreshing && <span className="ms-2 small text-secondary">Updating…</span>}
      </div>

      {mappedData.length === 0 ? (
        <div className="d-flex flex-column flex-grow-1 justify-content-center align-items-center fs-4 fw-bold text-secondary">
          No data available in this location
        </div>
      ) : (
        <Stack gap={3} className="pb-4">
          {mappedData.map((entry) => (
            <DropDownSpread
              key={entry.key}
              title={entry.title}
              data={entry.payload}
              dataIcons={ICONS}
            />
          ))}
        </Stack>
      )}
    </Container>
  );
}
